import type { EntityType } from "@app-types/entity-type";

export const SUPPORT_TARGET_TRAILER_VERSION = 1;

const SUPPORT_TARGET_TRAILER_PREFIX = "PATOLO_SUPPORT_TARGET";
const SUPPORT_TARGET_TRAILER_PATTERN =
  /(?:\r?\n){2}\[PATOLO_SUPPORT_TARGET:([A-Za-z0-9_-]+)\]\s*$/u;
const ANY_SUPPORT_TARGET_TRAILER_PATTERN =
  /(?:\r?\n){2}\[PATOLO_SUPPORT_TARGET:[^\]\r\n]*\]\s*$/u;
const BASE64_BLOCK_SIZE = 4;
const SUPPORT_REQUEST_TEMPLATE_SPACE_PATTERN = /[\s\u3000]+/gu;

export interface SupportTarget {
  v: typeof SUPPORT_TARGET_TRAILER_VERSION;
  type: EntityType;
  id: number;
  name: string;
  merchantId: number | null;
  merchantName: string | null;
}

export interface SupportTargetTrailerResult {
  displayText: string;
  supportTarget: SupportTarget | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isEntityType = (value: unknown): value is EntityType =>
  value === "user" || value === "lady";

const isNullableNumber = (value: unknown): value is number | null =>
  value === null || (typeof value === "number" && Number.isInteger(value));

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

/**
 * UTF-8 文字列を base64url に変換する。
 *
 * 日本語の会員名・クラブ名を含む JSON payload を URL や本文表示に紛れにくい
 * ASCII 文字列としてトレーラーへ格納するために使う。
 */
const encodeBase64Url = (value: string): string => {
  const binary = Array.from(new TextEncoder().encode(value), (byte) =>
    String.fromCodePoint(byte),
  ).join("");

  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
};

/**
 * base64url 文字列を UTF-8 文字列へ戻す。
 *
 * トレーラーはユーザー入力由来の本文に含まれるため、不正な payload でも例外を
 * 呼び出し元へ出さず `null` を返して表示本文の除去だけを続行できるようにする。
 */
const decodeBase64Url = (value: string): string | null => {
  const paddingLength =
    (BASE64_BLOCK_SIZE - (value.length % BASE64_BLOCK_SIZE)) %
    BASE64_BLOCK_SIZE;
  const base64 =
    value.replaceAll("-", "+").replaceAll("_", "/") + "=".repeat(paddingLength);

  try {
    const binary = atob(base64);
    const bytes = Uint8Array.from(
      binary,
      (character) => character.codePointAt(0) ?? 0,
    );

    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
};

/**
 * JSON parse 後の値がサポート対象 payload として扱えるか検証する。
 *
 * バックエンドはこの payload を解釈しないため、desk 側でリンク化できる最小限の
 * スナップショット項目だけを許可する。
 */
const readSupportTarget = (value: unknown): SupportTarget | null => {
  if (!isRecord(value)) return null;
  if (value.v !== SUPPORT_TARGET_TRAILER_VERSION) return null;
  if (!isEntityType(value.type)) return null;
  if (typeof value.id !== "number" || !Number.isInteger(value.id)) return null;
  if (typeof value.name !== "string") return null;
  if (!isNullableNumber(value.merchantId)) return null;
  if (!isNullableString(value.merchantName)) return null;

  return {
    v: SUPPORT_TARGET_TRAILER_VERSION,
    type: value.type,
    id: value.id,
    name: value.name,
    merchantId: value.merchantId,
    merchantName: value.merchantName,
  };
};

/**
 * サポート依頼テンプレート判定専用に文字列を正規化する。
 *
 * 会員が本文送信前に空白・改行・全角空白を挟んだり、区切りのコロンを半角へ
 * 変えたりしても、テンプレート本体が残っていれば対象情報を付与できるようにする。
 */
const normalizeSupportRequestTemplateText = (text: string): string =>
  text
    .replaceAll(":", "：")
    .replaceAll(SUPPORT_REQUEST_TEMPLATE_SPACE_PATTERN, "");

/**
 * 表示用テキストから末尾単独行のサポート対象トレーラーを取り除く。
 *
 * payload が壊れている場合でも、会員側・desk 側・一覧表示・通知本文に marker を
 * 出さないことを優先して marker 行だけを除去する。
 */
export const stripSupportTargetTrailer = (text: string): string =>
  text.replace(ANY_SUPPORT_TARGET_TRAILER_PATTERN, "");

/**
 * メッセージ本文の末尾へサポート対象 payload を base64url JSON として付与する。
 *
 * 既に同じ marker が残っている場合は二重付与を避けるため一度除去し、本文末尾の
 * 単独行という固定形式で保存する。
 */
export const appendSupportTargetTrailer = (
  text: string,
  supportTarget: SupportTarget,
): string => {
  const payload = encodeBase64Url(JSON.stringify(supportTarget));

  return `${stripSupportTargetTrailer(text)}\n\n[${SUPPORT_TARGET_TRAILER_PREFIX}:${payload}]`;
};

/**
 * 現在の入力本文がサポート依頼テンプレートとして残っているか判定する。
 *
 * ユーザーがテンプレート周辺に空白や改行を足した場合は許容するが、対象者名や
 * `相談内容` の構造が別物になった場合は通常メッセージとして扱う。
 */
export const isSupportRequestTemplateText = (
  text: string,
  supportTarget: SupportTarget,
): boolean =>
  normalizeSupportRequestTemplateText(text).startsWith(
    normalizeSupportRequestTemplateText(
      `サポート対象者：${supportTarget.name}さん相談内容：`,
    ),
  );

/**
 * メッセージ本文から表示用本文とサポート対象 payload を取り出す。
 *
 * 正常な marker なら `supportTarget` を返し、不正・非対応バージョン・型不一致の
 * marker なら本文から marker だけ除去して `supportTarget: null` として扱う。
 */
export const parseSupportTargetTrailer = (
  text: string | null | undefined,
): SupportTargetTrailerResult => {
  if (text == null) return { displayText: "", supportTarget: null };

  const matches = SUPPORT_TARGET_TRAILER_PATTERN.exec(text);
  if (matches === null) {
    return {
      displayText: stripSupportTargetTrailer(text),
      supportTarget: null,
    };
  }

  const json = decodeBase64Url(matches[1]);
  if (json === null) {
    return {
      displayText: stripSupportTargetTrailer(text),
      supportTarget: null,
    };
  }

  try {
    const parsed: unknown = JSON.parse(json);

    return {
      displayText: stripSupportTargetTrailer(text),
      supportTarget: readSupportTarget(parsed),
    };
  } catch {
    return {
      displayText: stripSupportTargetTrailer(text),
      supportTarget: null,
    };
  }
};

/**
 * desk の会員詳細へ直接遷移する URL を組み立てる。
 *
 * payload の `type` は `EntityType` に限定されているため、`user` は
 * `/agent/user/{id}`、`lady` は `/agent/lady/{id}` へ解決する。
 */
export const buildSupportTargetAgentUrl = (
  supportTarget: SupportTarget,
): string => `/agent/${supportTarget.type}/${supportTarget.id}`;
